import React, { useState } from 'react';
import { FileJson, Copy, Check, ArrowRightLeft, AlertCircle, Wand2 } from 'lucide-react';

// Simple YAML parser/stringifier (handles common cases)
const yamlToJson = (yaml: string): any => {
  const lines = yaml.split('\n');
  const result: any = {};
  const stack: { obj: any; indent: number }[] = [{ obj: result, indent: -1 }];
  
  for (const line of lines) {
    if (!line.trim() || line.trim().startsWith('#')) continue;
    
    const indent = line.search(/\S/);
    const content = line.trim();
    
    // Handle array items
    if (content.startsWith('- ')) {
      const value = content.slice(2).trim();
      while (stack.length > 1 && stack[stack.length - 1].indent >= indent) {
        stack.pop();
      }
      const parent = stack[stack.length - 1].obj;
      const lastKey = Object.keys(parent).pop();
      if (lastKey && !Array.isArray(parent[lastKey])) {
        parent[lastKey] = [];
      }
      if (lastKey) {
        parent[lastKey].push(parseYamlValue(value));
      }
      continue;
    }
    
    const colonIndex = content.indexOf(':');
    if (colonIndex === -1) continue;
    
    const key = content.slice(0, colonIndex).trim();
    const value = content.slice(colonIndex + 1).trim();
    
    while (stack.length > 1 && stack[stack.length - 1].indent >= indent) {
      stack.pop();
    }
    
    const current = stack[stack.length - 1].obj;
    
    if (value === '' || value === '|' || value === '>') {
      current[key] = {};
      stack.push({ obj: current[key], indent });
    } else {
      current[key] = parseYamlValue(value);
    }
  }
  
  return result;
};

const parseYamlValue = (value: string): any => {
  if (value === 'true') return true;
  if (value === 'false') return false;
  if (value === 'null' || value === '~') return null;
  if (/^-?\d+$/.test(value)) return parseInt(value);
  if (/^-?\d+\.\d+$/.test(value)) return parseFloat(value);
  if ((value.startsWith('"') && value.endsWith('"')) || 
      (value.startsWith("'") && value.endsWith("'"))) {
    return value.slice(1, -1);
  }
  return value;
};

const jsonToYaml = (obj: any, indent = 0): string => {
  const spaces = '  '.repeat(indent);
  let result = '';
  
  if (Array.isArray(obj)) {
    for (const item of obj) {
      if (typeof item === 'object' && item !== null) {
        result += `${spaces}- ${jsonToYaml(item, indent + 1).trim()}\n`;
      } else {
        result += `${spaces}- ${formatYamlValue(item)}\n`;
      }
    }
  } else if (typeof obj === 'object' && obj !== null) {
    for (const [key, value] of Object.entries(obj)) {
      if (typeof value === 'object' && value !== null) {
        result += `${spaces}${key}:\n${jsonToYaml(value, indent + 1)}`;
      } else {
        result += `${spaces}${key}: ${formatYamlValue(value)}\n`;
      }
    }
  }
  
  return result;
};

const formatYamlValue = (value: any): string => {
  if (value === null) return 'null';
  if (typeof value === 'string') {
    if (value.includes('\n') || value.includes(':') || value.includes('#')) {
      return `"${value.replace(/"/g, '\\"')}"`;
    }
    return value;
  }
  return String(value);
};

const JsonYamlConverter: React.FC = () => {
  const [json, setJson] = useState('');
  const [yaml, setYaml] = useState('');
  const [copied, setCopied] = useState<string | null>(null);
  const [error, setError] = useState('');

  const convertJsonToYaml = () => {
    try {
      setError('');
      const parsed = JSON.parse(json);
      setYaml(jsonToYaml(parsed));
    } catch (e) {
      setError('Invalid JSON');
    }
  };

  const convertYamlToJson = () => {
    try {
      setError('');
      const parsed = yamlToJson(yaml);
      setJson(JSON.stringify(parsed, null, 2));
    } catch (e) {
      setError('Invalid YAML');
    }
  };

  const formatJson = () => {
    try {
      setError('');
      const parsed = JSON.parse(json);
      setJson(JSON.stringify(parsed, null, 2));
    } catch (e) {
      setError('Invalid JSON');
    }
  };

  const minifyJson = () => {
    try {
      setError('');
      const parsed = JSON.parse(json);
      setJson(JSON.stringify(parsed));
    } catch (e) {
      setError('Invalid JSON');
    }
  };

  const copyToClipboard = async (content: string, type: string) => {
    await navigator.clipboard.writeText(content);
    setCopied(type);
    setTimeout(() => setCopied(null), 2000);
  };

  const clearAll = () => {
    setJson('');
    setYaml('');
    setError('');
  };

  const sampleJson = `{
  "name": "John Doe",
  "age": 30,
  "email": "john@example.com",
  "hobbies": ["reading", "gaming"],
  "address": {
    "city": "New York",
    "zip": "10001"
  }
}`;

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">JSON ↔ YAML Converter</h2>
        <p className="text-gray-600">Convert between JSON and YAML formats</p>
      </div>

      <div className="max-w-4xl mx-auto space-y-4">
        {/* Conversion Buttons */}
        <div className="flex justify-center gap-3">
          <button
            onClick={convertJsonToYaml}
            className="flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
          >
            JSON → YAML
          </button>
          <button
            onClick={convertYamlToJson}
            className="flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
          >
            YAML → JSON
          </button>
          <button
            onClick={formatJson}
            className="flex items-center gap-2 px-3 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
          >
            <Wand2 className="w-4 h-4" />
            Format
          </button>
          <button
            onClick={minifyJson}
            className="px-3 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
          >
            Minify
          </button>
          <button
            onClick={clearAll}
            className="px-3 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
          >
            Clear
          </button>
        </div>

        {/* Error */}
        {error && (
          <div className="flex items-center gap-2 bg-red-50 text-red-700 px-4 py-2 rounded-lg">
            <AlertCircle className="w-4 h-4" />
            {error}
          </div>
        )}

        {/* Input/Output */}
        <div className="grid md:grid-cols-2 gap-4">
          {/* JSON */}
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex justify-between items-center mb-2">
              <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <FileJson className="w-4 h-4 text-orange-500" />
                JSON
              </label>
              <div className="flex gap-1">
                <button
                  onClick={() => setJson(sampleJson)}
                  className="px-2 py-1 text-xs bg-gray-100 rounded hover:bg-gray-200"
                >
                  Sample
                </button>
                <button
                  onClick={() => copyToClipboard(json, 'json')}
                  className="p-1 text-gray-500 hover:text-gray-700"
                  disabled={!json}
                >
                  {copied === 'json' ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <textarea
              value={json}
              onChange={(e) => setJson(e.target.value)}
              className="w-full h-64 px-3 py-2 font-mono text-sm border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-orange-500"
              placeholder='{"key": "value"}'
            />
          </div>

          {/* YAML */}
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex justify-between items-center mb-2">
              <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <FileJson className="w-4 h-4 text-yellow-500" />
                YAML
              </label>
              <button
                onClick={() => copyToClipboard(yaml, 'yaml')}
                className="p-1 text-gray-500 hover:text-gray-700"
                disabled={!yaml}
              >
                {copied === 'yaml' ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
              </button>
            </div>
            <textarea
              value={yaml}
              onChange={(e) => setYaml(e.target.value)}
              className="w-full h-64 px-3 py-2 font-mono text-sm border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-orange-500"
              placeholder="key: value"
            />
          </div>
        </div>

        {/* Info */}
        <div className="bg-orange-50 rounded-xl p-4 border border-orange-100">
          <div className="flex items-start gap-3">
            <ArrowRightLeft className="w-5 h-5 text-orange-600 mt-0.5" />
            <div>
              <h4 className="font-medium text-orange-900 mb-2">JSON vs YAML</h4>
              <p className="text-sm text-orange-700">
                JSON uses braces and brackets with strict syntax. YAML uses indentation and is more human-readable.
                Both are commonly used for configuration files and data exchange.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JsonYamlConverter;
